<template lang="pug">
  .smart-table
    v-alert.mt-2(v-if="error" type="error" outlined) {{ error }}

    v-card(flat)
      table-search(
        v-if="!error && !hideSearch"
        :searchKeys="searchKeys"
        @search="loadItems"
        @clear="clearSearchKeys"
      )
        template(v-slot:search-fields="{ searchKeys, runSearch }")
          slot(name="search-fields" :searchKeys="searchKeys" :runSearch="runSearch")
        template(v-slot:expand-search-fields="{ searchKeys }")
          slot(name="expand-search-fields" :searchKeys="searchKeys")
        template(v-slot:search-actions="{ runSearch }")
          slot(name="search-actions" :runSearch="runSearch")

        template(v-slot:settings)
          table-settings(
            v-if="visibleHeaders"
            v-model="visibleHeaders"
            :headers="preparedHeaders"
          )

      .smart-table__wrapper
        v-data-table(
          v-if="!error && visibleHeaders"
          loader-height="3"
          v-bind="$attrs"
          :loading="loading"
          :headers="visibleHeaders"
          :headersLength="headersLength"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :hide-default-footer="loading || externalPagination"
          @update:page="loadItems"
          @update:items-per-page="loadItems"
          @update:sort-desc="loadItems"
        )
          // template(slot="headers" slot-scope="props")
          //   tr
          //     th.column(
          //       v-for="(t, idx) in visibleHeaders"
          //       :key="idx"
          //       :class="[t.pinned ? 'pinned' : '',\
          //         pagination.descending ? 'desc' : 'asc',\
          //         t.value === pagination.sortBy ? 'active' : '',\
          //         t.sortable === false ? '' : 'sortable']"
          //       @click="changeSort(t)"
          //     )
          //       slot(
          //         :name="`header--${t.value}`"
          //       )
          //         .d-inline
          //           span(v-if="t.value !== 'missed'") {{ props.headers[idx].text }}
          //           v-icon(v-if="t.sortable !== false" small) arrow_upward
          //     slot(name="actions-header")

          template(v-slot)

          template(v-slot:item="props")
            tr
              slot(name="row" v-bind:item="props.item")

                td(
                  v-for="(t, idx) in visibleHeaders"
                  :key="idx"
                  :class="{ pinned: t.pinned }"
                )
                  template(v-if="viewLink && t.value === VIEW_LINK_VALUE")
                    router-link(
                      :to="{ name: viewLink, params: { id: props.item.id } }"
                      :target="openInNewWindow ? '_blank' : '_self'"
                    )
                      v-icon(color="primary" small) remove_red_eye
                  slot(
                    v-else
                    :name="`item.${t.value}`"
                    :item="props.item"
                    :index="getRowNumber(props.index)"
                  )
                    span {{ prettifyField(props.item, t.value, getRowNumber(props.index)) }}

          template(slot="loading")
            v-skeleton-loader(type="table-tbody")

          template(slot="no-data")
            .text-xs-center Отсутствуют данные

          template(slot="no-results")
            .text-xs-center Не найдено подходящих данных

        .pt-4(v-if="externalPagination && items.length")
          v-pagination(
            v-model="pagination.page"
            :disabled="loading"
            :length="pages"
            total-visible="10"
          )

          //- v-select(
          //-   v-if="Array.isArray(perPageItems) && perPageItems.length"
          //-   v-model="pagination.itemsPerPage"
          //-   :items="perPageItems"
          //-   label="Количество элементов"
          //- )

</template>

<script>
import sum from 'hash-sum';

import TableSearch from './TableSearch.vue';
import TableSettings from './TableSettings.vue';

/*
* props:
* method            - функция(обязательная), принимает
                       { page<Номер старницы>, type<Тип вкладки>, search<JSON поисковых ключей> },
                       должна вернуть Promise c результатом запроса
* headers            - заголовки таблицы в формате [{ text, value }, {text, value }]
*/

const getTotalItemsFromResponse = ({ headers }) => Number(headers['x-pagination-total-count']);
const getPerPageFromResponse = ({ headers }) => Number(headers['x-pagination-per-page']);

export default {
  name: 'SmartTable',
  components: { TableSearch, TableSettings },
  props: {
    method: { type: Function, required: true },
    headers: { type: Array, default: () => [] },
    prettifyField: { type: Function, default: (item, key) => item[key] },
    externalPagination: { type: Boolean, default: false },
    viewLink: { type: String, default: '' },
    hideSearch: { type: Boolean, default: false },
  },
  data: () => ({
    items: [],
    visibleHeaders: null,
    localStorageToken: null,

    loading: false,
    error: null,

    totalItems: 0,
    pagination: {
      page: 1,
      sortBy: [],
      itemsPerPage: 15,
    },
    searchKeys: {},
    getTotalItemsFromResponse,
    getPerPageFromResponse,

    VIEW_LINK_VALUE: '-view-link',
  }),
  computed: {
    pages() {
      if (!this.pagination) return 1;
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage);
    },
    preparedHeaders() {
      const viewLinkHeader = { text: '', value: this.VIEW_LINK_VALUE, sortable: false };
      const additionalHeaders = this.viewLink ? [viewLinkHeader] : [];
      return [...additionalHeaders, ...this.headers];
    },
    headersLength() {
      let headersLength = this.headers.length;
      if (this.viewLink) headersLength += 1;
      if (this.$slots['actions-header']) headersLength += 1;
      return headersLength;
    },
    sortString() {
      const sortBy = this.pagination.sortBy[0];
      if (!sortBy) return null;
      return this.pagination.sortDesc[0] ? `-${sortBy}` : sortBy;
    },
  },
  watch: {
    visibleHeaders(val) {
      window.localStorage.setItem(this.localStorageToken, JSON.stringify(val));
    },
  },
  created() {
    this.init();
  },
  mounted() {
    this.loadCachedSettings();
  },
  methods: {
    init() {
      this.loadItems();
    },
    // Load SmartTable headers settings from local storage
    loadCachedSettings() {
      const headers = JSON.stringify(this.headers);
      const routepath = this.$route.path;
      const method = String(this.method);
      const localStorageToken = `smarttable_${sum(headers + routepath + method)}`;
      const cachedSettings = window.localStorage.getItem(localStorageToken);
      if (!cachedSettings) {
        this.visibleHeaders = this.headers;
      } else {
        this.visibleHeaders = JSON.parse(cachedSettings);
      }
      this.localStorageToken = localStorageToken;
    },
    clearSearchKeys() {
      this.pagination = null;
      this.loadItems();
    },
    getPaginationKeys() {
      const paginationKeys = {};
      if (this.pagination) {
        paginationKeys.page = this.pagination.page;
        paginationKeys['per-page'] = this.pagination.itemsPerPage;
      }
      return paginationKeys;
    },
    async loadItems() {
      this.loading = true;
      await this.$rHandler(
        this.method({
          sort: this.sortString,
          ...this.searchKeys,
          ...this.getPaginationKeys(),
        }),
        (resp) => {
          this.items = resp.data.data;
          this.totalItems = this.getTotalItemsFromResponse(resp) || 100;
          this.pagination.itemsPerPage = this.getPerPageFromResponse(resp) || 100;
          // Дает доступ к items родителю
          this.$emit('updated', this.items);
        },
        () => {
          this.error = 'Произошла ошибка, обратитесь к администратору';
        },
      );
      this.loading = false;
    },
    // changeSort(column) {
    //   if (column.sortable === false) return;
    //   if (this.pagination.sortBy === column.value) {
    //     this.pagination.descending = !this.pagination.descending;
    //   } else {
    //     this.pagination.sortBy = column.value;
    //     this.pagination.descending = false;
    //   }
    //   this.init();
    // },
    getRowNumber(index) {
      return this.pagination.itemsPerPage * (this.pagination.page - 1) + index + 1;
    },
    refresh() {
      this.loadItems();
    },
  },
};
</script>

<style lang="scss">
.smart-table-list .v-list {
  .v-list-item {
    font-size: 13px;
    font-weight: 400;
    height: 40px;
  }
  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
    margin: 0;
  }
}

.smart-table {
  &__wrapper {
    min-height: 300px;
  }

  table.v-table {
    th {
      text-align: left;
      transition: none;
    }

    .pinned {
      left: 0;
      z-index: 1;
      position: sticky;
      top: auto;
      background: red;
      box-shadow: inset -4px 0px 0 red;
      transition: none;
    }
    .view-link {
      position: absolute;
      width: 100px;
    }
  }
}

.smart-table .loading-table tr:hover {
  background: red;
}
</style>
